import { useCallback, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import clsx from 'clsx';
import { Text } from '@clay-run/glaze/text';
import { Textarea } from '@clay-run/glaze/textarea';
import { effectiveNoteTextMax, normalizeNoteText, noteTargetKey, type NoteMention, type NoteTarget } from '@shared/flow/notes';
import { ComposerActionButton } from '../chat/ComposerActionButton';
import { useFlowNotesStore } from './flow-notes-store';
import { MentionTint } from './MentionTint';
import { MentionPicker, pruneMentions, type MentionKeyHandler } from './MentionPicker';
import { usePostNote } from './useFlowNotes';

/** What a mention picker (U8b) is handed: the live text and the two ways to change it. */
export interface MentionPickerApi {
  value: string;
  /** Caret offset in `value`. */
  caret: number;
  /** Replaces the text, optionally moving the caret (default: the browser's). */
  setValue: (next: string, caret?: number) => void;
  mentions: NoteMention[];
  setMentions: (next: NoteMention[]) => void;
  /** Lets the picker see keys first; return true to stop the composer handling them (Enter posts). */
  registerKeyHandler: (handler: MentionKeyHandler | null) => void;
}

export interface NoteComposerProps {
  /** Where the note will be written. */
  target: NoteTarget;
  /** The acting user's Salesforce display name; the length limit depends on it. */
  myName: string | null;
  /** Replaces the default `@` picker (tests, other hosts). */
  renderMentionPicker?: (api: MentionPickerApi) => ReactNode;
  onMentionsChange?: (mentions: NoteMention[]) => void;
}

const NO_MENTIONS: NoteMention[] = [];

/** Renders the picker as its own component so the composer's refs are only touched from handlers. */
function MentionSlot({ render, api }: { render: (api: MentionPickerApi) => ReactNode; api: MentionPickerApi }) {
  return <>{render(api)}</>;
}

/**
 * Floating note composer. The draft lives only in `useFlowNotesStore.drafts`
 * (keyed by the target), so it survives the rail closing, the target switching
 * and the composer remounting. Enter posts, Shift+Enter is a newline.
 */
export function NoteComposer({ target, myName, renderMentionPicker, onMentionsChange }: NoteComposerProps) {
  const key = noteTargetKey(target);
  const storeDraft = useFlowNotesStore(s => s.drafts[key] ?? '');
  const mentions = useFlowNotesStore(s => s.draftMentions[key] ?? NO_MENTIONS);
  const saving = useFlowNotesStore(s => s.pending?.status === 'saving');
  // The zustand store is the ONLY copy of the draft. A second local copy (useDraftState) went stale
  // when the composer remounted mid-post: the accepted-post clear hit the store while the new
  // instance kept its own seeded text. The store is module-scope, so SSE-driven parent re-renders
  // cannot reset it.
  const value = storeDraft;
  const { mutate } = usePostNote();
  const [caretState, setCaret] = useState(0);
  // A draft replaced by a shorter one (accepted post, destination change) keeps the caret in bounds; empty means 0.
  const caret = Math.min(caretState, value.length);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const tintRef = useRef<HTMLDivElement>(null);
  const pendingCaret = useRef<number | null>(null);
  const keyHandler = useRef<MentionKeyHandler | null>(null);
  const registerKeyHandler = useCallback((handler: MentionKeyHandler | null) => {
    keyHandler.current = handler;
  }, []);

  const limit = effectiveNoteTextMax(myName ?? '');
  const atLimit = value.length >= limit;
  // The limit can shrink once the rep's name loads; an older, longer draft is kept but not sent.
  const overLimit = value.length > limit;

  const setMentions = (next: NoteMention[]) => {
    useFlowNotesStore.getState().setDraftMentions(key, next);
    onMentionsChange?.(next);
  };

  const setValue = (next: string, nextCaret?: number) => {
    const clipped = next.slice(0, limit);
    if (nextCaret !== undefined) {
      pendingCaret.current = Math.min(nextCaret, clipped.length);
      setCaret(pendingCaret.current);
    }
    useFlowNotesStore.getState().setDraft(key, clipped);
    // A mention whose @label was edited out stops counting (toward the cap too) right away.
    const kept = pruneMentions(clipped, mentions);
    if (kept.length !== mentions.length) setMentions(kept);
  };

  // After a mention is inserted React rewrites the field, which parks the caret at the end.
  useLayoutEffect(() => {
    if (pendingCaret.current === null) return;
    textareaRef.current?.setSelectionRange(pendingCaret.current, pendingCaret.current);
    pendingCaret.current = null;
  });

  const defaultPicker = (api: MentionPickerApi) => <MentionPicker accountId={target.accountId} {...api} />;

  const submit = () => {
    if (saving || overLimit || normalizeNoteText(value) === '') return;
    const notes = value.trim();
    // Text and store draft are cleared only once the server ACCEPTS the post, so a
    // refusal or failure leaves the rep's words in the field.
    mutate(
      { target, notes, clientNoteId: crypto.randomUUID(), mentions: pruneMentions(notes, mentions), source: 'composer' },
      {
        onAccepted: () => {
          // Store only: this callback can run after the composer unmounted.
          useFlowNotesStore.getState().clearDraft(key);
        },
      },
    );
    // Posting to a record other than the open one moves the pane there, so the
    // pending bubble and its confirmation are on screen.
    const open = useFlowNotesStore.getState().target;
    if (open && noteTargetKey(open) !== key) useFlowNotesStore.getState().openTarget(target);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (keyHandler.current?.(event)) return;
    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return;
    event.preventDefault();
    submit();
  };

  return (
    <div className="flex flex-col gap-1">
      <MentionSlot
        render={renderMentionPicker ?? defaultPicker}
        api={{ value, caret, setValue, mentions, setMentions, registerKeyHandler }}
      />
      <div
        data-testid="note-composer-frame"
        className="flex flex-col rounded-2xl border border-border-primary bg-bg-primary shadow-sm transition-colors focus-within:border-border-action"
      >
        <div className="relative">
          <MentionTint ref={tintRef} value={value} mentions={mentions} />
          <Textarea
            borderless
            resizable={false}
            rows={2}
            spellCheck={false}
            aria-label="Note"
            placeholder="Add a note"
            value={value}
            disabled={saving}
            // The text is painted by MentionTint behind the field so mention tokens can be
            // coloured; the field keeps the value, caret, selection and IME. The caret stays visible.
            className="max-h-40 overflow-y-auto [&_textarea]:text-transparent [&_textarea]:caret-content-primary"
            ref={textareaRef}
            onChange={event => {
              setValue(event.target.value);
              setCaret(event.target.selectionStart);
            }}
            onScroll={event => {
              if (tintRef.current) tintRef.current.scrollTop = event.currentTarget.scrollTop;
            }}
            onSelect={event => setCaret(event.currentTarget.selectionStart)}
            onKeyDown={onKeyDown}
          />
        </div>
        <div className="flex items-center justify-between gap-2 px-3 pb-2">
          <div className="ml-auto flex shrink-0 items-center gap-2">
            <Text size="xs" variant="tertiary" className={clsx(atLimit && 'text-content-danger')}>
              {`${value.length}/${limit}`}
            </Text>
            <ComposerActionButton
              compact={false}
              isStreaming={false}
              canSubmitWhileStreaming={false}
              isSubmitting={saving}
              sendDisabled={saving || overLimit || normalizeNoteText(value) === ''}
              submitLabel="Post note"
              onSubmit={submit}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
